import { CustomAvatar, ItemActions } from '@europrocurement/flexy-components';
import { Box, Checkbox, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type UtilisateurTaches } from '@europrocurement/l2d-domain';
import EditableTaskPriorityChip from '../EditableTaskPriorityChip/EditableTaskPriorityChip';
import useFadeOverflow, { FADE_MASK_SX } from '../../hook/useFadeOverflow';
import CheckableTaskRowDatesSection from './CheckableTaskRowDatesSection';
import CheckableTaskRowCreatorSection from './CheckableTaskRowCreatorSection';

type CheckableTaskRowProps = {
    task: DossierTaches | PrescripteurTaches | UtilisateurTaches;
    handleCheckTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onEditTask: (linkedTask: DossierTaches | PrescripteurTaches | UtilisateurTaches) => void;
    onDeleteTask: (id: number) => void;
    canDeleteTask: boolean;
    linkToEntity?: React.ReactNode;
    onPriorityChange?: (
        task: DossierTaches | PrescripteurTaches | UtilisateurTaches,
        newPriority: number,
    ) => void;
};

const CheckableTaskRow: React.FunctionComponent<CheckableTaskRowProps> = function ({
    task,
    handleCheckTask,
    onEditTask,
    onDeleteTask,
    canDeleteTask = false,
    linkToEntity,
    onPriorityChange,
}) {
    const { palette } = useTheme();
    const [isHovered, setIsHovered] = useState(false);
    const [isScrollFocused, setIsScrollFocused] = useState(false);
    const [containerWidth, setContainerWidth] = useState<number>(9999);

    const outerRef = useRef<HTMLDivElement>(null);
    const descriptionContainerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { ref: titleRef, isOverflowing: isTitleOverflowing } = useFadeOverflow<HTMLElement>([
        containerWidth,
        task.tache?.title,
    ]);

    useEffect(() => {
        const el = outerRef.current;
        if (!el) return undefined;
        const observer = new ResizeObserver(([entry]) => {
            setContainerWidth(entry.contentRect.width);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                descriptionContainerRef.current &&
                !descriptionContainerRef.current.contains(event.target as Node)
            ) {
                setIsScrollFocused(false);
            }
        };
        const handleGlobalWheel = (event: WheelEvent) => {
            if (isScrollFocused && scrollContainerRef.current) {
                const container = scrollContainerRef.current;
                const { scrollTop, scrollHeight, clientHeight } = container;
                const canScrollDown = scrollTop < scrollHeight - clientHeight;
                const canScrollUp = scrollTop > 0;
                if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) {
                    event.preventDefault();
                    event.stopPropagation();
                    container.scrollTop = Math.max(
                        0,
                        Math.min(scrollTop + event.deltaY * 0.4, scrollHeight - clientHeight),
                    );
                } else if (
                    (event.deltaY > 0 && !canScrollDown) ||
                    (event.deltaY < 0 && !canScrollUp)
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        };
        if (isScrollFocused) {
            document.addEventListener('click', handleClickOutside);
            document.addEventListener('wheel', handleGlobalWheel, {
                passive: false,
                capture: true,
            });
        }
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('wheel', handleGlobalWheel, true);
        };
    }, [isScrollFocused]);

    if (!task.tache) {
        return <Box />;
    }

    const { assignTo } = task.tache;
    const isCompact = containerWidth < 450;
    const isMini = containerWidth < 280;

    let footerContent: React.ReactNode;
    if (isMini || isCompact) {
        footerContent = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <CheckableTaskRowDatesSection
                    expiredAt={task.tache.expiredAt}
                    remindAt={task.tache.remindAt}
                    isMini={isMini}
                />
                {linkToEntity && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>{linkToEntity}</Box>
                )}
                <CheckableTaskRowCreatorSection
                    createdBy={task.tache.createdBy}
                    createdAt={task.tache.createdAt}
                    isMini={isMini}
                />
            </Box>
        );
    } else {
        footerContent = (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <CheckableTaskRowDatesSection
                        expiredAt={task.tache.expiredAt}
                        remindAt={task.tache.remindAt}
                        isMini={false}
                    />
                    {linkToEntity}
                </Box>
                <Box sx={{ marginLeft: 'auto', marginRight: '4px' }}>
                    <CheckableTaskRowCreatorSection
                        createdBy={task.tache.createdBy}
                        createdAt={task.tache.createdAt}
                        isMini={false}
                    />
                </Box>
            </Box>
        );
    }

    const handleDescriptionKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!scrollContainerRef.current) return;
        const container = scrollContainerRef.current;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            container.scrollTop = Math.min(
                container.scrollTop + 40,
                container.scrollHeight - container.clientHeight,
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            container.scrollTop = Math.max(container.scrollTop - 40, 0);
        } else if (e.key === 'Escape') {
            setIsScrollFocused(false);
            descriptionContainerRef.current?.blur();
        }
    };

    const descriptionEl = (
        <Box
            ref={descriptionContainerRef}
            tabIndex={0}
            role="region"
            aria-label="Description de la tâche, utilisez les flèches pour défiler"
            onClick={() => setIsScrollFocused(true)}
            onFocus={() => setIsScrollFocused(true)}
            onBlur={(e) => {
                if (
                    e.relatedTarget !== null &&
                    !descriptionContainerRef.current?.contains(e.relatedTarget as Node)
                ) {
                    setIsScrollFocused(false);
                }
            }}
            onKeyDown={handleDescriptionKeyDown}
            sx={{
                border: '2px solid',
                borderColor: isScrollFocused ? palette.primary.main : 'transparent',
                borderRadius: '6px',
                transition: 'border-color 0.2s ease-in-out',
                cursor: isScrollFocused ? 'default' : 'pointer',
                outline: 'none',
            }}
        >
            <Box
                ref={scrollContainerRef}
                sx={{ maxHeight: '80px', overflowY: 'auto', overflowX: 'hidden', outline: 'none' }}
            >
                <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap' }}
                >
                    {task.tache.description}
                </Typography>
            </Box>
        </Box>
    );

    const assigneeEl = assignTo ? (
        <Tooltip title={`Assignée à ${assignTo.prenom ?? ''} ${assignTo.nom ?? ''}`.trim()}>
            <Box>
                <CustomAvatar
                    firstName={assignTo.prenom ?? undefined}
                    lastName={assignTo.nom ?? undefined}
                    keycloakId={assignTo.userIdentifier ?? null}
                    sx={{ width: 24, height: 24, fontSize: '0.65rem' }}
                />
            </Box>
        </Tooltip>
    ) : null;

    const priorityChipEl = (
        <EditableTaskPriorityChip
            taskPriority={task.tache.priority}
            onPriorityChange={
                onPriorityChange ? (newPriority) => onPriorityChange(task, newPriority) : undefined
            }
            compact={isMini}
        />
    );

    const itemActionsProps = {
        visible: isHovered,
        onEdit: () => onEditTask(task),
        onDelete: canDeleteTask && task.id ? () => onDeleteTask(task.id as number) : undefined,
        confirmation: true as const,
    } as const;

    const titleEl = (
        <Tooltip
            title={task.tache.title}
            disableHoverListener={!isTitleOverflowing}
        >
            <Typography
                ref={titleRef}
                variant="body1"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    textDecoration: task.tache.isDone ? 'line-through' : 'none',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    ...(isTitleOverflowing && FADE_MASK_SX),
                }}
            >
                {task.tache.title}
            </Typography>
        </Tooltip>
    );

    let topContent: React.ReactNode;
    if (isMini) {
        topContent = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Row 1: assignee + chip + actions + checkbox */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {assigneeEl}
                    {priorityChipEl}
                    <Box
                        sx={{
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        <ItemActions
                            orientation="row"
                            slideDirection="left"
                            {...itemActionsProps}
                        />
                        <Checkbox
                            size="small"
                            checked={task.tache.isDone}
                            onChange={() => handleCheckTask(task)}
                            sx={{ padding: '4px' }}
                            inputProps={{ 'aria-label': 'task-checkbox' }}
                        />
                    </Box>
                </Box>
                {/* Row 2: title (fade) */}
                {titleEl}
                {/* Row 3: description */}
                {descriptionEl}
            </Box>
        );
    } else if (isCompact) {
        topContent = (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* Row 1: assignee + priority + actions horizontal + checkbox */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {assignTo && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Typography
                                variant="l2dcaption"
                                sx={{ fontWeight: 400 }}
                            >
                                Assignée à
                            </Typography>
                            {assigneeEl}
                        </Box>
                    )}
                    {priorityChipEl}
                    <Box
                        sx={{
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0,
                        }}
                    >
                        <ItemActions
                            orientation="row"
                            slideDirection="left"
                            {...itemActionsProps}
                        />
                        <Checkbox
                            size="small"
                            checked={task.tache.isDone}
                            onChange={() => handleCheckTask(task)}
                            sx={{ padding: '4px' }}
                            inputProps={{ 'aria-label': 'task-checkbox' }}
                        />
                    </Box>
                </Box>
                {/* Row 2: title */}
                <Typography
                    variant="body1"
                    sx={{ textDecoration: task.tache.isDone ? 'line-through' : 'none' }}
                >
                    {task.tache.title}
                </Typography>
                {/* Row 3: description */}
                {descriptionEl}
            </Box>
        );
    } else {
        topContent = (
            <Box sx={{ display: 'flex', gap: '4px' }}>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography
                            variant="body1"
                            sx={{
                                flex: 1,
                                textDecoration: task.tache.isDone ? 'line-through' : 'none',
                            }}
                        >
                            {task.tache.title}
                        </Typography>
                        {assignTo && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Typography
                                    variant="l2dcaption"
                                    sx={{ fontWeight: 400 }}
                                >
                                    Assignée à
                                </Typography>
                                {assigneeEl}
                            </Box>
                        )}
                        {priorityChipEl}
                    </Box>
                    {descriptionEl}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '38px',
                        flexShrink: 0,
                    }}
                >
                    <Checkbox
                        size="small"
                        checked={task.tache.isDone}
                        onChange={() => handleCheckTask(task)}
                        sx={{ padding: '4px' }}
                        inputProps={{ 'aria-label': 'task-checkbox' }}
                    />
                    <ItemActions
                        orientation="column"
                        slideDirection="right"
                        {...itemActionsProps}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            ref={outerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                border: `1px solid ${palette.grey['200']}`,
                borderRadius: '8px',
                padding: '8px 12px 8px 16px',
                width: '100%',
                overflow: 'hidden',
                boxShadow: `0px 2px 2.5px 0px ${palette.background.shadow}`,
            }}
        >
            {topContent}
            {footerContent}
        </Box>
    );
};

export default CheckableTaskRow;
